import { enums } from "../../enums/enums";
import { AreaServicio } from "../../entities/catalogos/AreaServicio";
import { ICliente } from "../../entities/catalogos/ICliente";
import { IServicioAdicional, ServicioAdicional } from "../../entities/catalogos/ServicioAdicional";
import { IRecoleccion } from "../../redux/types/recoleccionActionTypes";

type ModalidadCargoCita = "normal" | "fueraHorario" | "cedis" | null;

const CARGOS_CITA_RAD = {
    normal: "CARGO POR CITA RAD",
    fueraHorario: "FLETE CARGO RAD FUERA DE HORARIO",
    cedis: "CARGO POR CITA RAD CEDIS"
} as const;

const obtenerNombreServicio = (servicio: ServicioAdicional | IServicioAdicional): string =>
    servicio.nombre ?? servicio.concepto?.nombreConcepto ?? "";

const copiarServicio = (servicio: ServicioAdicional | IServicioAdicional): ServicioAdicional => {
    const props = { ...(servicio as IServicioAdicional) };
    const copia = new ServicioAdicional(props);
    copia.convenioId = props.convenioId ?? 0;
    copia.tieneConvenio = !!(servicio as ServicioAdicional).tieneConvenio;
    return copia;
};

export const calcularAceptaCitaRadOrigen = (
    cliente: ICliente,
    areaServicio?: AreaServicio,
    esServicioInternacional = false
): boolean =>
    !esServicioInternacional && !!(cliente?.esRadConCita && areaServicio?.esRadConCita);

export const prepararAreaServicioOrigenGuia = (areaServicio: AreaServicio): AreaServicio => {
    const copia = new AreaServicio({ ...areaServicio });
    copia.ObtenerGrupoHorarios();
    return copia;
};

export const prepararAreaServicioDestinoGuia = prepararAreaServicioOrigenGuia;

export const calcularAceptaCitaEadDestino = (
    cliente: ICliente,
    areaServicio?: AreaServicio,
    esServicioInternacional = false
): boolean =>
    !esServicioInternacional && !!(cliente?.esEadConCita && areaServicio?.esEadConCita);

export const origenGuiaTieneModalidadRecoleccion = (recoleccion: IRecoleccion): boolean => {
    if (recoleccion.tipoRecoleccionId === enums.tipoRecoleccion.ocurre) {
        return true;
    }
    if (recoleccion.EsFueraHorario && recoleccion.fechaCita) {
        return true;
    }
    if (recoleccion.fechaRecoleccion) {
        return true;
    }
    if (recoleccion.fechaCita) {
        return true;
    }
    if (recoleccion.fechaCitaCedis) {
        return true;
    }
    return false;
};

export const sincronizarCargoPorCitaGuiaOrigen = (
    recoleccion: IRecoleccion,
    modalidad: ModalidadCargoCita
): ServicioAdicional[] => {
    const nombreSeleccionado = modalidad ? CARGOS_CITA_RAD[modalidad] : undefined;
    const nombresDeCitaSet = new Set<string>(Object.values(CARGOS_CITA_RAD));
    const serviciosBase = (recoleccion.serviciosAdicionales?.length
        ? recoleccion.serviciosAdicionales
        : (recoleccion.areaServicio?.serviciosAdicionales ?? [])) as ServicioAdicional[];
    const fuentes: Array<ServicioAdicional[] | undefined> = [
        recoleccion.serviciosAdicionales as ServicioAdicional[] | undefined,
        recoleccion.areaServicio?.serviciosAdicionales as ServicioAdicional[] | undefined
    ];
    const nombresAgregados = new Set<string>();
    let cargoSeleccionadoAgregado = false;

    const serviciosSinDuplicados = serviciosBase.reduce((resultado: ServicioAdicional[], servicio: ServicioAdicional) => {
        const nombreServicio = obtenerNombreServicio(servicio);
        if (!nombresDeCitaSet.has(nombreServicio)) {
            resultado.push(servicio);
            return resultado;
        }
        if (nombresAgregados.has(nombreServicio)) {
            return resultado;
        }
        nombresAgregados.add(nombreServicio);
        const copiaServicio = copiarServicio(servicio);
        const debeSeleccionarse = nombreServicio === nombreSeleccionado;
        copiaServicio.seleccionado = debeSeleccionarse;
        copiaServicio.aplicaOrigen = debeSeleccionarse;
        copiaServicio.aplicaDestino = false;
        cargoSeleccionadoAgregado = cargoSeleccionadoAgregado || debeSeleccionarse;
        resultado.push(copiaServicio);
        return resultado;
    }, []);

    if (nombreSeleccionado && !cargoSeleccionadoAgregado) {
        let servicioCatalogo: ServicioAdicional | undefined;
        fuentes.some(fuente => {
            servicioCatalogo = fuente?.find(s => obtenerNombreServicio(s) === nombreSeleccionado);
            return !!servicioCatalogo;
        });
        if (servicioCatalogo) {
            const copiaServicio = copiarServicio(servicioCatalogo);
            copiaServicio.seleccionado = true;
            copiaServicio.aplicaOrigen = true;
            copiaServicio.aplicaDestino = false;
            serviciosSinDuplicados.push(copiaServicio);
        }
    }

    return serviciosSinDuplicados;
};

const baseOrigenComercial = (recoleccion: IRecoleccion): IRecoleccion => ({
    ...recoleccion,
    tipoRecoleccionId: enums.tipoRecoleccion.comercial
});

export const aplicarRecoleccionNormalGuiaOrigen = (
    recoleccion: IRecoleccion,
    fechaRecoleccion: Date | null
): IRecoleccion => {
    let next = baseOrigenComercial({
        ...recoleccion,
        fechaRecoleccion: fechaRecoleccion ?? undefined,
        aceptaCita: recoleccion.aceptaCita,
        tipoDeRecoleccionOrigen: 1,
        completado: !!fechaRecoleccion
    });
    delete next.fechaCita;
    delete next.fechaCitaCedis;
    delete next.EsFueraHorario;
    delete next.horaFHDe;
    delete next.horaFHHasta;
    delete next.EsCitaRadCedis;
    next.serviciosAdicionales = sincronizarCargoPorCitaGuiaOrigen(next, null);
    return next;
};

export const aplicarCitaRadGuiaOrigen = (
    recoleccion: IRecoleccion,
    fechaCita: Date
): IRecoleccion => {
    let next = baseOrigenComercial({
        ...recoleccion,
        fechaCita,
        aceptaCita: true,
        tipoDeRecoleccionOrigen: 2,
        completado: true
    });
    delete next.fechaRecoleccion;
    delete next.fechaCitaCedis;
    delete next.EsFueraHorario;
    delete next.horaFHDe;
    delete next.horaFHHasta;
    delete next.EsCitaRadCedis;
    next.serviciosAdicionales = sincronizarCargoPorCitaGuiaOrigen(next, "normal");
    return next;
};

export const aplicarCitaFueraHorarioGuiaOrigen = (
    recoleccion: IRecoleccion,
    fechaCita: Date,
    horaDe: string,
    horaHasta: string
): IRecoleccion => {
    let next = baseOrigenComercial({
        ...recoleccion,
        fechaCita,
        aceptaCita: true,
        EsFueraHorario: true,
        horaFHDe: horaDe,
        horaFHHasta: horaHasta,
        tipoDeRecoleccionOrigen: 3,
        completado: true
    });
    delete next.fechaRecoleccion;
    delete next.fechaCitaCedis;
    delete next.EsCitaRadCedis;
    next.serviciosAdicionales = sincronizarCargoPorCitaGuiaOrigen(next, "fueraHorario");
    return next;
};

const CARGOS_CITA_EAD = {
    normal: "CARGO POR CITA EAD",
    fueraHorario: "FLETE CARGO EAD FUERA DE HORARIO",
    cedis: "CARGO POR CITA EAD CEDIS"
} as const;

export const limpiarModalidadEntregaGuiaDestino = (recoleccion: IRecoleccion): IRecoleccion => {
    const next = { ...recoleccion };
    delete next.fechaCita;
    delete next.fechaRecoleccion;
    delete next.fechaCitaCedis;
    delete next.EsFueraHorario;
    delete next.horaFHDe;
    delete next.horaFHHasta;
    delete next.tipoDeRecoleccionDestino;
    delete next.EsCitaEadCedis;
    return next;
};

export const destinoGuiaTieneModalidadEntrega = (recoleccion: IRecoleccion): boolean => {
    if (recoleccion.tipoRecoleccionId === enums.tipoRecoleccion.ocurre) {
        return !!(recoleccion.sucursal?.id);
    }
    if (recoleccion.sinAreaServicio || !(recoleccion.areaServicio?.id)) {
        return false;
    }
    const modalidad = recoleccion.tipoDeRecoleccionDestino;
    if (!modalidad) {
        return false;
    }
    if (modalidad === 1) {
        return true;
    }
    if (modalidad === 2) {
        return !!recoleccion.fechaCita && !recoleccion.EsFueraHorario;
    }
    if (modalidad === 3) {
        return !!(recoleccion.fechaCita && recoleccion.EsFueraHorario);
    }
    return false;
};

export const sincronizarCargoPorCitaGuiaDestino = (
    recoleccion: IRecoleccion,
    modalidad: ModalidadCargoCita
): ServicioAdicional[] => {
    const nombreSeleccionado = modalidad ? CARGOS_CITA_EAD[modalidad] : undefined;
    const nombresDeCitaSet = new Set<string>(Object.values(CARGOS_CITA_EAD));
    const fuentes: Array<ServicioAdicional[] | undefined> = [
        recoleccion.serviciosAdicionales as ServicioAdicional[] | undefined,
        recoleccion.areaServicio?.serviciosAdicionales as ServicioAdicional[] | undefined
    ];
    const configurados = (recoleccion.serviciosAdicionales ?? []) as ServicioAdicional[];

    if (configurados.length > 0) {
        const nombresAgregados = new Set<string>();
        let cargoSeleccionadoAgregado = false;
        const resultado = configurados.reduce((acc: ServicioAdicional[], servicio: ServicioAdicional) => {
            const nombreServicio = obtenerNombreServicio(servicio);
            if (!nombresDeCitaSet.has(nombreServicio)) {
                acc.push(copiarServicio(servicio));
                return acc;
            }
            if (nombresAgregados.has(nombreServicio)) {
                return acc;
            }
            nombresAgregados.add(nombreServicio);
            const copiaServicio = copiarServicio(servicio);
            const debeSeleccionarse = nombreServicio === nombreSeleccionado;
            copiaServicio.seleccionado = debeSeleccionarse;
            copiaServicio.aplicaOrigen = false;
            copiaServicio.aplicaDestino = debeSeleccionarse;
            cargoSeleccionadoAgregado = cargoSeleccionadoAgregado || debeSeleccionarse;
            acc.push(copiaServicio);
            return acc;
        }, []);

        if (nombreSeleccionado && !cargoSeleccionadoAgregado) {
            let servicioCatalogo: ServicioAdicional | undefined;
            fuentes.some(fuente => {
                servicioCatalogo = fuente?.find(s => obtenerNombreServicio(s) === nombreSeleccionado);
                return !!servicioCatalogo;
            });
            if (servicioCatalogo) {
                const copiaServicio = copiarServicio(servicioCatalogo);
                copiaServicio.seleccionado = true;
                copiaServicio.aplicaOrigen = false;
                copiaServicio.aplicaDestino = true;
                resultado.push(copiaServicio);
            }
        }
        return resultado;
    }

    const serviciosBase = (recoleccion.areaServicio?.serviciosAdicionales ?? []) as ServicioAdicional[];
    const nombresAgregados = new Set<string>();
    let cargoSeleccionadoAgregado = false;

    const serviciosSinDuplicados = serviciosBase.reduce((resultado: ServicioAdicional[], servicio: ServicioAdicional) => {
        const nombreServicio = obtenerNombreServicio(servicio);
        if (!nombresDeCitaSet.has(nombreServicio)) {
            resultado.push(copiarServicio(servicio));
            return resultado;
        }
        if (nombresAgregados.has(nombreServicio)) {
            return resultado;
        }
        nombresAgregados.add(nombreServicio);
        const copiaServicio = copiarServicio(servicio);
        const debeSeleccionarse = nombreServicio === nombreSeleccionado;
        copiaServicio.seleccionado = debeSeleccionarse;
        copiaServicio.aplicaOrigen = false;
        copiaServicio.aplicaDestino = debeSeleccionarse;
        cargoSeleccionadoAgregado = cargoSeleccionadoAgregado || debeSeleccionarse;
        resultado.push(copiaServicio);
        return resultado;
    }, []);

    if (nombreSeleccionado && !cargoSeleccionadoAgregado) {
        let servicioCatalogo: ServicioAdicional | undefined;
        fuentes.some(fuente => {
            servicioCatalogo = fuente?.find(s => obtenerNombreServicio(s) === nombreSeleccionado);
            return !!servicioCatalogo;
        });
        if (servicioCatalogo) {
            const copiaServicio = copiarServicio(servicioCatalogo);
            copiaServicio.seleccionado = true;
            copiaServicio.aplicaOrigen = false;
            copiaServicio.aplicaDestino = true;
            serviciosSinDuplicados.push(copiaServicio);
        }
    }

    return serviciosSinDuplicados;
};

export const aplicarEntregaNormalGuiaDestino = (recoleccion: IRecoleccion): IRecoleccion => {
    let next = limpiarModalidadEntregaGuiaDestino(recoleccion);
    next = {
        ...next,
        tipoDeRecoleccionDestino: 1,
        completado: true
    };
    delete next.EsCitaEadCedis;
    next.serviciosAdicionales = sincronizarCargoPorCitaGuiaDestino(next, null);
    return next;
};

export const aplicarCitaEadGuiaDestino = (
    recoleccion: IRecoleccion,
    fechaCita: Date
): IRecoleccion => {
    let next = limpiarModalidadEntregaGuiaDestino(recoleccion);
    next = {
        ...next,
        fechaCita,
        aceptaCita: true,
        tipoDeRecoleccionDestino: 2,
        EsFueraHorario: false,
        completado: true
    };
    delete next.EsCitaEadCedis;
    next.serviciosAdicionales = sincronizarCargoPorCitaGuiaDestino(next, "normal");
    return next;
};

export const aplicarCitaFueraHorarioGuiaDestino = (
    recoleccion: IRecoleccion,
    fechaCita: Date,
    horaDe: string,
    horaHasta: string
): IRecoleccion => {
    let next = limpiarModalidadEntregaGuiaDestino(recoleccion);
    next = {
        ...next,
        fechaCita,
        aceptaCita: true,
        EsFueraHorario: true,
        horaFHDe: horaDe,
        horaFHHasta: horaHasta,
        tipoDeRecoleccionDestino: 3,
        completado: true
    };
    next.serviciosAdicionales = sincronizarCargoPorCitaGuiaDestino(next, "fueraHorario");
    return next;
};
